import { useState, useEffect, useCallback } from 'react';
import { DateTime } from 'luxon';
import { Typography, Box, Card, CardContent, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { EventsActivityChart } from './EventsActivityChart';
import { ApiCallsActivityChart } from './ApiCallsActivityChart';
import { RetryQueueList } from './RetryQueueList';
import { EventDetailsDialog } from './EventDetailsDialog';
import {
    monitorApi,
    MonitorData,
    EventStatsResponse,
    ApiCallStatsResponse,
    RetryEvent,
} from '../services/monitor-api.service';
import { useAuth } from '../contexts/AuthContext';

export function Monitor() {
    const [data, setData] = useState<MonitorData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
    const [activityData, setActivityData] = useState<EventStatsResponse | null>(null);
    const [activityLoading, setActivityLoading] = useState(true);
    const [activityError, setActivityError] = useState<string | null>(null);
    const [apiCallsActivityData, setApiCallsActivityData] = useState<ApiCallStatsResponse | null>(null);
    const [apiCallsActivityLoading, setApiCallsActivityLoading] = useState(true);
    const [apiCallsActivityError, setApiCallsActivityError] = useState<string | null>(null);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
    const [retryingEventId, setRetryingEventId] = useState<number | null>(null);
    const [markingDeadEventId, setMarkingDeadEventId] = useState<number | null>(null);
    const [markingHandledEventId, setMarkingHandledEventId] = useState<number | null>(null);
    const [markingWarningEventId, setMarkingWarningEventId] = useState<number | null>(null);
    const { email } = useAuth();

    const fetchMonitorData = useCallback(async () => {
        try {
            const monitorData = await monitorApi.getMonitorData();
            setData(monitorData);
            setError(null);
            setLastUpdated(new Date());
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : (err as { message?: string })?.message || 'An error occurred';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchActivityData = useCallback(async () => {
        try {
            const stats = await monitorApi.getEventStats(24);
            setActivityData(stats);
            setActivityError(null);
        } catch (err) {
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : (err as { message?: string })?.message || 'Failed to load activity data';
            setActivityError(errorMessage);
        } finally {
            setActivityLoading(false);
        }
    }, []);

    const fetchApiCallsActivityData = useCallback(async () => {
        try {
            const stats = await monitorApi.getApiCallStats(24);
            setApiCallsActivityData(stats);
            setApiCallsActivityError(null);
        } catch (err) {
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : (err as { message?: string })?.message || 'Failed to load API calls activity data';
            setApiCallsActivityError(errorMessage);
        } finally {
            setApiCallsActivityLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMonitorData();
        fetchActivityData();
        fetchApiCallsActivityData();
        const interval = setInterval(fetchMonitorData, 5000);
        // Activity data refreshes less frequently (every 60 seconds)
        const activityInterval = setInterval(fetchActivityData, 60000);
        const apiCallsActivityInterval = setInterval(fetchApiCallsActivityData, 60000);
        return () => {
            clearInterval(interval);
            clearInterval(activityInterval);
            clearInterval(apiCallsActivityInterval);
        };
    }, [fetchMonitorData, fetchActivityData, fetchApiCallsActivityData]);

    const handleRefresh = () => {
        setLoading(true);
        setActivityLoading(true);
        setApiCallsActivityLoading(true);
        fetchMonitorData();
        fetchActivityData();
        fetchApiCallsActivityData();
    };

    const getNextRetryTime = (nextRetryTime: string | null): string => {
        if (!nextRetryTime) return 'N/A';

        const diffSeconds = DateTime.fromISO(nextRetryTime).diff(DateTime.utc(), 'seconds').seconds | 0;

        if (diffSeconds < 0) return 'Now';
        if (diffSeconds < 60) return `${diffSeconds}s`;
        if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m`;
        return `${Math.floor(diffSeconds / 3600)}h ${Math.floor((diffSeconds % 3600) / 60)}m`;
    };

    const getRetryChipColor = (retryCount: number): 'default' | 'info' | 'warning' | 'error' => {
        if (retryCount >= 7) return 'error';
        if (retryCount >= 5) return 'warning';
        if (retryCount >= 3) return 'info';
        return 'default';
    };

    const handleViewEventDetails = (retryEvent: RetryEvent) => {
        setSelectedEventId(retryEvent.id);
    };

    const handleCloseDialog = () => {
        setSelectedEventId(null);
    };

    const handleRetryEvent = async (eventId: number) => {
        try {
            setRetryingEventId(eventId);
            await monitorApi.forceRetry(eventId);
            fetchMonitorData();
        } catch (err) {
            console.error('Failed to retry event:', err);
        } finally {
            setRetryingEventId(null);
        }
    };

    const handleMarkDeadEvent = async (eventId: number) => {
        try {
            setMarkingDeadEventId(eventId);
            await monitorApi.markDead(eventId, email || undefined);
            fetchMonitorData();
        } catch (err) {
            console.error('Failed to mark event as dead:', err);
        } finally {
            setMarkingDeadEventId(null);
        }
    };

    const handleMarkHandledEvent = async (eventId: number) => {
        try {
            setMarkingHandledEventId(eventId);
            await monitorApi.markHandled(eventId, email || undefined);
            fetchMonitorData();
        } catch (err) {
            console.error('Failed to mark event as handled:', err);
        } finally {
            setMarkingHandledEventId(null);
        }
    };

    const handleMarkWarningEvent = async (eventId: number) => {
        try {
            setMarkingWarningEventId(eventId);
            await monitorApi.markWarning(eventId, email || undefined);
            fetchMonitorData();
        } catch (err) {
            console.error('Failed to mark event as warning:', err);
        } finally {
            setMarkingWarningEventId(null);
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Updated: {lastUpdated.toLocaleTimeString()}
                </Typography>
                <Tooltip title="Refresh">
                    <span>
                        <IconButton onClick={handleRefresh} color="primary" disabled={loading} sx={{ ml: 1 }}>
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {loading && !data ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            ) : (
                <>
                    {/* Stats Cards */}
                    <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, mb: 4, flexWrap: 'wrap' }}>
                        <StatCard title="Active Events" value={data?.activeCount ?? 0} color="primary.main" />
                        <StatCard
                            title="Retry Queue"
                            value={data?.retryCount ?? 0}
                            color={data?.retryCount ? 'warning.main' : 'success.main'}
                        />
                    </Box>

                    {/* Retry Queue Table */}
                    {data?.retryQueue && data.retryQueue.length > 0 && (
                        <>
                            <Typography variant="h2" sx={{ mb: 2 }}>
                                Retry Queue
                            </Typography>

                            <RetryQueueList
                                events={data.retryQueue}
                                getNextRetryTime={getNextRetryTime}
                                getRetryChipColor={getRetryChipColor}
                                onRetry={handleRetryEvent}
                                onViewDetails={handleViewEventDetails}
                            />
                        </>
                    )}

                    {/* Events Activity Chart */}
                    <Box sx={{ mt: 4 }}>
                        <EventsActivityChart data={activityData} loading={activityLoading} error={activityError} />
                    </Box>

                    {/* API Calls Activity Chart */}
                    <Box sx={{ mt: 4 }}>
                        <ApiCallsActivityChart
                            data={apiCallsActivityData}
                            loading={apiCallsActivityLoading}
                            error={apiCallsActivityError}
                        />
                    </Box>
                </>
            )}

            {/* Event Details Dialog */}
            <EventDetailsDialog
                eventId={selectedEventId}
                open={!!selectedEventId}
                onClose={handleCloseDialog}
                onRetry={handleRetryEvent}
                onMarkDead={handleMarkDeadEvent}
                onMarkHandled={handleMarkHandledEvent}
                onMarkWarning={handleMarkWarningEvent}
                retryingEventId={retryingEventId}
                markingDeadEventId={markingDeadEventId}
                markingHandledEventId={markingHandledEventId}
                markingWarningEventId={markingWarningEventId}
            />
        </>
    );
}

// Stat Card Component
interface StatCardProps {
    title: string;
    value: number;
    color: string;
}

function StatCard({ title, value, color }: StatCardProps) {
    return (
        <Card sx={{ minWidth: 140, flex: '1 1 140px', maxWidth: 140 }}>
            <CardContent
                sx={{
                    p: { xs: 1.5, md: 2 },
                    '&:last-child': { pb: { xs: 1.5, md: 2 } },
                    textAlign: 'center',
                }}
            >
                <Typography
                    variant="h3"
                    sx={{ color, fontWeight: 700, fontSize: { xs: '2rem', md: '2.5rem' }, mb: 0.5 }}
                >
                    {value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {title}
                </Typography>
            </CardContent>
        </Card>
    );
}
